// src/components/CoreCanvas.jsx

import React, { useMemo, useCallback } from "react";
import { ReactFlow, ConnectionMode } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./GraphCanvas.css";
import { CANVAS_MAX_ZOOM } from "../utils/constants";

export default function CoreCanvas({
  wrapperRef,
  onInit,                  // just a setter for the RF instance in App.js
  nodes,
  onNodesChange,
  edges,
  onEdgesChange,
  nodeTypes,
  defaultEdgeOptions,
  connectionLineType,
  connectionLineStyle,
  proOptions,
  onConnectStart,
  onConnect,
  onConnectEnd,
  onNodeDragStop,
  onNodesDelete,
  onEdgesDelete,
  onEdgeClick,
  onEdgeContextMenu,
  onNodeMouseEnter,
  onNodeMouseLeave,
  onNodeClick,
  onNodeDoubleClick,
  onNodeContextMenu,
  onPaneContextMenu,
  onDragStart,
  onDragOver,
  onDrop,
  onCanvasClick,
  nodesDraggable = false,
  nodesConnectable = false,
  children,
  minZoom = 0.1,
  maxZoom = CANVAS_MAX_ZOOM,
  edgeTypes,
  isValidConnection,
  // Multi-select (Shift+drag box, Shift/Cmd+click) for save-as-template.
  onSelectionChange,
  selectionKeyCode = "Shift",
  // The viewport started to pan or zoom. The template editor closes its right-click menu on it;
  // nothing else passes it.
  onMoveStart,
}) {
  const memoNodeTypes = useMemo(() => nodeTypes, [nodeTypes]);

  // onInit simply forwards the instance upward; no fitView here.
  const handleInit = useCallback(
    (instance) => {
      onInit?.(instance);
    },
    [onInit]
  );

  return (
    <div
      className="reactflow-wrapper"
      ref={wrapperRef}
      style={{ width: "100%", height: "100%" }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(e);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.(e);
      }}
      onClick={(e) => onCanvasClick?.(e)}
    >
      <ReactFlow
        onInit={handleInit}
        nodes={nodes}
        onNodesChange={onNodesChange}
        edges={edges}
        onEdgesChange={onEdgesChange}
        nodeTypes={memoNodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        connectionLineType={connectionLineType}
        connectionLineStyle={connectionLineStyle}
        proOptions={proOptions}
        connectionMode={ConnectionMode.Loose}
        onConnectStart={onConnectStart}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        onNodeDragStop={onNodeDragStop}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onEdgeClick={onEdgeClick}
        onEdgeContextMenu={onEdgeContextMenu}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeContextMenu={onNodeContextMenu}
        onPaneContextMenu={onPaneContextMenu}
        onDragStart={onDragStart}
        nodesDraggable={nodesDraggable}
        nodesConnectable={nodesConnectable}
        onSelectionChange={onSelectionChange}
        onMoveStart={onMoveStart}
        selectionKeyCode={selectionKeyCode}
        multiSelectionKeyCode={["Shift", "Meta", "Control"]}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        zoomOnDoubleClick={false}
        minZoom={minZoom}
        maxZoom={maxZoom}
        edgeTypes={edgeTypes}
        isValidConnection={isValidConnection}
      >
        {children}
      </ReactFlow>
    </div>
  );
}